import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuTriangleAlert, LuCopy, LuMessageCircle, LuLock } from 'react-icons/lu';
import api from '../../services/api';
import ReservarDiaSheet from './ReservarDiaSheet';
import { diaLargo } from './diasRuta';

// Semana del portal (25-oct-2026, reemplaza a "Ruta"). Cada día con su zona y cuánto lugar queda, y los pedidos
// de la empresa: confirmados con franja (ya se cerró el día), reservados (provisorios hasta las 17:30 del día
// anterior) y pedidos a Dispenser La Tienda. Lo que se corrió de día trae el mensaje listo para el cliente.
// Abajo, lo que no tiene día, urgentes primero. La empresa solo ve lo suyo.
const franjaTxt = f => (f && /^\d{2}:\d{2}-\d{2}:\d{2}$/.test(f) ? `${Number(f.slice(0, 2))} a ${Number(f.slice(6, 8))}` : f || '');
const ESTADO = {
    CONFIRMADO: { texto: 'Confirmado', clase: 'bg-[#134E4A] text-[#CCFBF1]' },
    RESERVADO: { texto: 'Reservado', clase: 'bg-chip text-secondary' },
    PIDE: { texto: 'Pedido a Dispenser', clase: 'bg-[#3A2A00] text-[#F0C14B]' },
};
const ZONA_CLASE = { CABA: 'bg-[#1E3A8A] text-[#DBEAFE]', SUR: 'bg-[#134E4A] text-[#CCFBF1]' };
const chip = 'h-5 px-1.5 rounded-md text-[11px] font-black uppercase inline-flex items-center shrink-0';

function AvisoCliente({ p }) {
    const copiar = async () => {
        try { await navigator.clipboard.writeText(p.aviso); toast.success('Mensaje copiado'); } catch { toast.error('No se pudo copiar'); }
    };
    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="text-label font-bold text-[#A16207] dark:text-[#F0C14B]">Pasó del {diaLargo(p.movidoDesde)}: avisale al cliente</span>
            <button type="button" onClick={copiar} className="h-8 px-2.5 rounded-lg bg-chip text-ink text-label font-black inline-flex items-center gap-1"><LuCopy size={13} /> Copiar</button>
            <a href={`https://wa.me/?text=${encodeURIComponent(p.aviso)}`} target="_blank" rel="noreferrer"
                className="h-8 px-2.5 rounded-lg bg-[#16A34A] text-white text-label font-black inline-flex items-center gap-1"><LuMessageCircle size={13} /> WhatsApp</a>
        </div>
    );
}

// Sugerencia de día de Dispenser La Tienda (26-oct-2026): la empresa la acepta o lo deja como estaba
function Sugerencia({ p, onListo }) {
    const [enviando, setEnviando] = useState(false);
    const responder = async (aceptar) => {
        setEnviando(true);
        try {
            await api.post('/empresa/semana/sugerencia', { pedidoId: p.pedidoId, aceptar });
            toast.success(aceptar ? `Pasado al ${p.sugerencia.dia}` : 'Queda como estaba');
            onListo();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); setEnviando(false); }
    };
    return (
        <div className="mt-2 p-3 rounded-xl bg-[rgba(14,165,233,0.10)] space-y-2">
            <p className="text-caption text-ink">
                <b>Dispenser La Tienda sugiere el {p.sugerencia.dia}</b>{p.sugerencia.nota ? `: ${p.sugerencia.nota}` : ''}
            </p>
            <div className="flex gap-1.5">
                <button type="button" disabled={enviando} onClick={() => responder(true)} className="h-9 px-3 rounded-lg bg-[#0284C7] text-white text-label font-black disabled:opacity-50">Aceptar</button>
                <button type="button" disabled={enviando} onClick={() => responder(false)} className="h-9 px-3 rounded-lg bg-chip text-ink text-label font-black disabled:opacity-50">Dejar como está</button>
            </div>
        </div>
    );
}

function FilaPedido({ p, onCambiar, onListo }) {
    const e = ESTADO[p.estado];
    return (
        <div className="px-4 py-3 border-t border-black/[0.06] dark:border-white/[0.06]">
            <div className="flex items-center gap-3">
                {p.estado === 'CONFIRMADO' && (
                    <span className="w-14 shrink-0 text-label font-black text-ink leading-tight">{franjaTxt(p.franja)}</span>
                )}
                <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5 min-w-0">
                        {p.urgente && <span className={`${chip} bg-brand-red text-white`}>Urgente</span>}
                        <span className="text-body font-black text-ink truncate">{p.nombre}</span>
                    </span>
                    <span className="block text-caption text-muted truncate">{[p.direccion, p.motivo, p.equipos > 1 ? `${p.equipos} equipos` : null].filter(Boolean).join(' · ')}</span>
                    {e && <span className={`${chip} mt-1 ${e.clase}`}>{e.texto}</span>}
                </span>
                {p.estado === 'RESERVADO' && (
                    <button type="button" onClick={onCambiar} className="h-9 px-3 shrink-0 rounded-xl bg-chip text-ink text-label font-black active:scale-95">Cambiar</button>
                )}
                {p.estado === 'CONFIRMADO' && <LuLock size={14} className="shrink-0 text-muted" aria-label="Horario confirmado" />}
            </div>
            {p.aviso && <AvisoCliente p={p} />}
            {p.sugerencia && <Sugerencia p={p} onListo={onListo} />}
        </div>
    );
}

export default function SemanaEmpresa({ onCambio }) {
    const [datos, setDatos] = useState(null);
    const [eligiendo, setEligiendo] = useState(null);
    const cargar = useCallback(async () => {
        try { const r = await api.get('/empresa/semana'); setDatos(r.data); } catch { toast.error('No se pudo cargar la semana'); setDatos({ dias: [], sinDia: [] }); }
    }, []);
    useEffect(() => { cargar(); }, [cargar]);
    if (!datos) return <p className="py-16 text-center text-muted text-body">Cargando…</p>;

    const caja = 'rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] overflow-hidden';
    const dias = datos.dias.filter(d => d.trabaja);
    const urgentesSinDia = datos.sinDia.filter(p => p.urgente).length;
    const nSugerencias = [...datos.sinDia, ...datos.dias.flatMap(d => d.pedidos)].filter(p => p.sugerencia).length;
    return (
        <div className="space-y-4 max-w-2xl">
            <p className="text-caption text-muted px-1">
                Cada día se trabaja una zona. Lo reservado es provisorio: a las {datos.horaCierre || '17:30'} del día anterior se arma la ruta y te confirmamos el horario de cada uno.
            </p>

            {nSugerencias > 0 && (
                <p className="px-3.5 py-3 rounded-2xl bg-[rgba(14,165,233,0.10)] text-caption text-ink">
                    <b>Tenés {nSugerencias} sugerencia{nSugerencias !== 1 ? 's' : ''} de día de Dispenser La Tienda.</b> Aceptala o dejalo como está: si no respondés, vale lo que elegiste.
                </p>
            )}

            {datos.sinDia.length > 0 && (
                <section className="space-y-2">
                    <h3 className="px-1 text-label font-black text-muted uppercase tracking-widest">
                        Sin día · {datos.sinDia.length}{urgentesSinDia > 0 && <span className="text-brand-red"> · {urgentesSinDia} urgente{urgentesSinDia !== 1 ? 's' : ''}</span>}
                    </h3>
                    <div className={caja}>
                        {datos.sinDia.map(p => (
                            <div key={p.pedidoId} className="flex items-center gap-3 px-4 py-3 border-t first:border-t-0 border-black/[0.06] dark:border-white/[0.06]">
                                <span className="flex-1 min-w-0">
                                    <span className="flex items-center gap-1.5 min-w-0">
                                        {p.urgente && <LuTriangleAlert size={14} className="shrink-0 text-brand-red" aria-label="Urgente" />}
                                        <span className="text-body font-black text-ink truncate">{p.nombre}</span>
                                    </span>
                                    <span className="block text-caption text-muted truncate">{[p.direccion, p.motivo].filter(Boolean).join(' · ')}</span>
                                    {p.zonaNombre && <span className={`${chip} mt-1 ${ZONA_CLASE[p.zona] || 'bg-chip text-secondary'}`}>{p.zonaNombre}</span>}
                                    {p.aviso && <AvisoCliente p={p} />}
                                    {p.sugerencia && <Sugerencia p={p} onListo={() => { cargar(); onCambio && onCambio(); }} />}
                                </span>
                                <button type="button" onClick={() => setEligiendo(p)} className="h-10 px-3.5 shrink-0 rounded-xl bg-brand-red text-white text-label font-black active:scale-95">Elegir día</button>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className="space-y-2">
                <h3 className="px-1 text-label font-black text-muted uppercase tracking-widest">Próximos días</h3>
                {dias.map(d => (
                    <div key={d.fecha} className={caja}>
                        <div className="flex items-center gap-2 px-4 py-3">
                            <span className="flex-1 min-w-0">
                                <span className="block text-body-lg font-black text-ink capitalize">{d.dia}</span>
                                <span className="block text-caption text-muted">
                                    {d.cerrado ? 'Ruta armada' : d.libres > 0 ? `${d.libres} lugar${d.libres !== 1 ? 'es' : ''} libre${d.libres !== 1 ? 's' : ''}` : 'Completo · solo urgencias'}
                                </span>
                            </span>
                            <span className={`${chip} h-6 ${ZONA_CLASE[d.zona] || 'bg-chip text-secondary'}`}>{d.zonaNombre}</span>
                        </div>
                        {d.pedidos.map(p => <FilaPedido key={p.pedidoId} p={p} onCambiar={() => setEligiendo({ ...p, rutaFecha: d.fecha })} onListo={() => { cargar(); onCambio && onCambio(); }} />)}
                    </div>
                ))}
            </section>

            {eligiendo && <ReservarDiaSheet pedido={eligiendo} onCerrar={() => setEligiendo(null)}
                onListo={() => { setEligiendo(null); cargar(); onCambio && onCambio(); }} />}
        </div>
    );
}
